// Utilidades COMPARTIDAS de precios de planes para la web pública (página de
// Planes y auto-registro). Centralizar el formateo y el cálculo del equivalente
// mensual evita que las dos pantallas muestren cifras distintas para el mismo
// plan. `price_cents_yearly` es el TOTAL ANUAL (12 meses prepagados), no una
// tarifa mensual; por eso para mostrar un "/mes" comparable lo dividimos entre 12.

import type { PublicPlan } from './publicApi.js';

/** Cadencia de facturación elegible por el usuario en la web. */
export type Billing = 'monthly' | 'annual';

/** Formatea centavos a un precio legible en la moneda del plan. */
export function formatPrice(cents: number, currency: string): string {
  if (!cents || cents <= 0) return 'Gratis';
  const amount = cents / 100;
  const symbol = currency === 'USD' ? '$' : '';
  // Sin decimales si es entero; dos si no.
  const value = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${symbol}${value}`;
}

/**
 * Precio EQUIVALENTE POR MES de un plan según el periodo elegido, en centavos.
 *
 * `price_cents_yearly` guarda el precio ANUAL TOTAL (12 meses pagados por
 * adelantado), no una tarifa mensual. Para mostrar siempre una cifra "/mes"
 * comparable, en el periodo anual dividimos ese total entre 12. Así "Anual" no
 * muestra el total del año como si fuera el cobro mensual.
 */
export function monthlyEquivalentCents(plan: PublicPlan, billing: Billing): number {
  if (billing === 'annual' && plan.price_cents_yearly > 0) {
    return Math.round(plan.price_cents_yearly / 12);
  }
  return plan.price_cents_monthly;
}

/**
 * Importe TOTAL que se cobrará en el primer cargo según la cadencia, en centavos:
 * el total anual en "annual" (si existe) o el precio mensual en "monthly". Es el
 * valor real que verá el usuario en la pasarela, por eso lo mostramos en el alta.
 */
export function chargeTotalCents(plan: PublicPlan, billing: Billing): number {
  if (billing === 'annual' && plan.price_cents_yearly > 0) {
    return plan.price_cents_yearly;
  }
  return plan.price_cents_monthly;
}

/**
 * Porcentaje de ahorro del plan anual frente al mensual (0–100). Compara el
 * precio mensual real contra el equivalente por mes del anual. Devuelve 0 si no
 * hay ahorro o faltan precios.
 */
export function annualSavingPercent(plan: PublicPlan): number {
  if (plan.price_cents_monthly <= 0 || plan.price_cents_yearly <= 0) return 0;
  const annualPerMonth = plan.price_cents_yearly / 12;
  if (annualPerMonth >= plan.price_cents_monthly) return 0;
  return Math.round((1 - annualPerMonth / plan.price_cents_monthly) * 100);
}
